import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  DocumentNode,
  DefaultOptions,
  OperationVariables,
  gql,
} from '@apollo/client/core';
import { ElMessage } from 'element-plus';
import store from '../store/index';
import { refreshToken } from './auth';
import { checkIsAccessTokenExpired, isOpenWhenSignedOut } from '../router/authGuard';
import { isEmbeddedExperience } from '@/utils/embeddedUtils';
import { decodeCauseNames } from '@/utils/htmlEntities';
import {
  createSessionRecovery, isAuthRejected, SESSION_ENDED_MESSAGE, signInPathFor,
} from '@/utils/authSession';

const baseURL = import.meta.env.VITE_GRAPHQL_BASE_URL;

const httpLink = createHttpLink({
  uri: baseURL,
});

const cache = new InMemoryCache();
const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
};

// Benevity cause names arrive HTML-encoded; decode them once for every screen (SPO-1301).
const decodeCauseNamesLink = new ApolloLink((operation, forward) => forward(operation)
  .map((result) => (result.data ? { ...result, data: decodeCauseNames(result.data) } : result)));

export const gqlClient = new ApolloClient({
  link: decodeCauseNamesLink.concat(httpLink),
  cache,
  defaultOptions,
});

async function send(
  type: 'query' | 'mutation',
  query: DocumentNode,
  variables: OperationVariables | undefined,
  context: any,
  token: string | null,
): Promise<any> {
  const headers = {
    ...context?.headers,
    authorization: token ? `Bearer ${token}` : '',
  };

  if (type === 'query') {
    return gqlClient.query({ query, variables, context: { ...context, headers } });
  }
  if (type === 'mutation') {
    return gqlClient.mutate({ mutation: query, variables, context: { ...context, headers } });
  }
  throw new Error(`Invalid request type: ${type}`);
}

const sessionCheck = gql`query SessionCheck {
  profile {
    id
  }
}`;

async function endSession(): Promise<void> {
  store.dispatch('users/resetMe');
  // Imported lazily: the router imports this module through authGuard.
  const { default: router } = await import('@/router/index');
  const route = router.currentRoute.value;
  if (isEmbeddedExperience({ path: route.path, query: route.query as Record<string, unknown> })) {
    return;
  }

  ElMessage.warning(SESSION_ENDED_MESSAGE);
  if (!isOpenWhenSignedOut(route)) {
    router.replace(signInPathFor(route.fullPath));
  }
}

const handleRejectedToken = createSessionRecovery({
  getToken: () => store.getters['users/getToken'],
  getRefreshToken: () => store.getters['users/getRefreshToken'],
  isTokenAccepted: async (token) => {
    try {
      const response = await send('query', sessionCheck, undefined, undefined, token);
      return !isAuthRejected(response);
    } catch {
      // A network failure says nothing about the token; never sign out over it.
      return true;
    }
  },
  refresh: async (refreshTokenValue) => {
    const response = await send(
      'mutation', refreshToken, { token: refreshTokenValue }, undefined, null,
    );
    return response?.data?.refreshToken ?? null;
  },
  setToken: (token) => store.commit('users/SET_NEW_ACCESS_TOKEN', token),
  endSession: () => { endSession(); },
});

export async function makeRequest(
  type: 'query' | 'mutation',
  query: DocumentNode,
  variables: OperationVariables | undefined,
  context: any,
): Promise<any> {
  try {
    await checkIsAccessTokenExpired();

    // Honor explicit token: null = anonymous; omit token key = use store
    const token = context && 'token' in context
      ? context.token
      : store.getters['users/getToken'];

    // Mutations throw on GraphQL errors instead of returning them.
    let response;
    try {
      response = await send(type, query, variables, context, token);
    } catch (error: any) {
      if (!error?.graphQLErrors?.length) throw error;
      response = { errors: error.graphQLErrors, thrown: error };
    }

    // Only the signed-in user's own token can end their session. The request was refused before it
    // ran, so repeating it (mutations included) is safe.
    if (token && token === store.getters['users/getToken'] && isAuthRejected(response)) {
      const { retryWith } = await handleRejectedToken(token);
      if (retryWith) {
        return send(type, query, variables, context, retryWith);
      }
    }

    if (response.thrown) throw response.thrown;
    return response;
  } catch (error) {
    console.error('GraphQL request error:', error);
    throw error;
  }
}
